(ns webworkers.metadata
  (:require [webworkers.sql-keywords :as sql-keywords]
            [clojure.string]
            [minorodata.localforage.core :refer [db]]
            [applied-science.js-interop :as js-interop]))

;; TODO: store response in IndexedDB (take into account different
;; warehouses and possibly multiple user logins at the same time).
;;
;; IndexedDB is shared between main thread and webworkers, so it should
;; be very efficient for sending large bits of data around, since
;; 1. You can't store state in a webworker — browser can restart it
;;    whenever it feels like
;; 2. Sending messages to webworkers means serialisation and
;;    deserialisation, which is inefficient (albeit very fast)



;; ----------------------------------------------------------------------------
;;
;; DB TREE GENERATOR
;;
;; Creates a precomputed nested structure for easier rendering of database
;; tree view.
;;
;; ----------------------------------------------------------------------------

(defn make-tree [catalogs schemas tables columns]
  (let [tree  (reduce (fn [tree catalog]
                        (assoc tree (:table-cat catalog)
                               (assoc catalog
                                      :schemas {}
                                      :type "catalog")))
                      {}
                      catalogs)
        tree' (reduce (fn [tree schema]
                        (let [{:keys [table-catalog table-cat table-schem]} schema]
                          (assoc-in tree [(or table-catalog table-cat) :schemas table-schem]
                                    (assoc schema
                                           :tables {}
                                           :type "schema"))))
                      tree
                      schemas)
        tree'' (reduce (fn [tree table]
                         (let [{:keys [table-cat table-catalog table-schem table-name]} table]
                           (assoc-in tree
                                     [(or table-cat table-catalog) :schemas table-schem :tables table-name]
                                     (assoc table
                                            :columns []
                                            :type "table"))))
                       tree'
                       tables)


        ;; In redshift there is a weird bug with metadata. Columns with `pg_toast`
        ;; schema are being returned, while no `pg_toast` is present in the list of
        ;; schemas.
        ;;
        ;; `pg_toast` is an internal schema in postgres, and by extension in
        ;; redshift, that stores some sort of indexing information.
        ;;
        ;; It shouldn't be dispayed in the ui.

        tree''' (reduce
                 (fn [tree column]
                   (let [{:keys [table-cat table-catalog table-schem table-name]} column

                         catalog (or table-cat table-catalog)
                         path    [catalog :schemas table-schem :tables table-name :columns]]

                     (update-in tree path conj (assoc column :type "column"))))
                 tree''
                 columns)]
    (clj->js tree''')))


(defn generate-tree-view [type]
  (js-interop/call db :getItem "db-metadata"
                   (fn [error data]

                     (when error
                       (js/console.warn error))

                     (let [data' (js->clj data :keywordize-keys true)
                           {:keys [tables columns schemas catalogs]} data'

                           tree (make-tree catalogs schemas tables columns)]
                       (js-interop/call db :setItem "db-tree" tree (fn []
                                                                     (js/postMessage #js {:type type :data {}})))))))


;; ----------------------------------------------------------------------------
;;
;; COMPLETIONS GENERATOR
;;
;; Computes a set of records for faster autocomplete suggestions search.
;;
;; ----------------------------------------------------------------------------


(defn generate-metadata-key [metadata]
  (clojure.string/join
   "---"
   [(or (:table-cat metadata)
        (:table-catalog metadata ))
    (:table-schem metadata )
    (:table-name metadata )
    (:column-name metadata )]))

(defn generate-metadata-key-native [metadata]
  (clojure.string/join
   "---"
   [(or (js-interop/get metadata :table-cat)
        (js-interop/get metadata :table-catalog))
    (js-interop/get metadata :table-schem)
    (js-interop/get metadata :table-name)
    (js-interop/get metadata :column-name)]))


(def generate-metadata-key-mem
  (memoize generate-metadata-key))


(defn add-metadata-key [metadata]
  (let [id (generate-metadata-key-mem metadata)]
    (assoc metadata :id id)))


(defn prepare-metadata [type item]
  (let [{:keys [column-name type-name table-name table-schem table-cat table-catalog table-type char-octet-length]} item

        catalog   (or table-cat
                      table-catalog)

        full-data {:table-type        (or table-type "")
                   :table-schem       (or table-schem "")
                   :table-name        (or table-name "")
                   :type-name         (or type-name "")
                   :column-name       (or column-name "")
                   :char-octet-length char-octet-length
                   :table-cat         catalog
                   :type              type}

        metadata-key   (generate-metadata-key full-data)]

    (assoc full-data :id metadata-key)))


(defn make-metadata-maps [type items]
  (mapv (partial prepare-metadata type) items))


(defn prepare-static-completion-map [type item]
  {:type  type
   :id    item
   :value item})


(defn make-static-completion-maps [type items]
  (mapv (partial prepare-static-completion-map type) items))


(defn generate-completions [type]
  (js-interop/call db :getItem "db-metadata"
                   (fn [error data]

                     (when error
                       (js/console.warn error))

                     (let [data' (js->clj data :keywordize-keys true)
                           {:keys [tables columns schemas catalogs]} data'

                           tables'   (make-metadata-maps "table" tables)
                           columns'  (make-metadata-maps "column" columns)
                           schemas'  (make-metadata-maps "schema" schemas)
                           catalogs' (make-metadata-maps "catalog" catalogs)

                           keywords  (make-static-completion-maps "keyword"  sql-keywords/keywords)
                           functions (make-static-completion-maps "function" sql-keywords/functions)
                           operators (make-static-completion-maps "operator" sql-keywords/operators)

                           nodes       (concat catalogs' schemas' tables' columns')
                           completions (concat nodes keywords functions operators)]
                       (js-interop/call db :setItem "db-completions" (clj->js completions)
                                        (fn [error]
                                          (when error
                                            (js/console.log error))
                                          (js/postMessage #js {:type type :data {}})))))))




;; ----------------------------------------------------------------------------
;;
;; COMPLETIONS MATCHER
;;
;; Finds matching completions for given input.
;;
;; Icon kinds are ids from monaco.languages.CompletionItemKind.KEYWORD
;;
;; ----------------------------------------------------------------------------


(defmulti suggestion-data-from-metadata (fn [metadata]
                                          (js-interop/get metadata :type)))


(defmethod suggestion-data-from-metadata "catalog" [metadata]
  (let [table-cat (js-interop/get metadata :table-cat)]
    {:label      table-cat
     :insertText table-cat
     :detail     "Catalog"
     :kind       23}))


(defmethod suggestion-data-from-metadata "schema" [metadata]
  (let [table-cat (js-interop/get metadata :table-cat)
        table-schem (js-interop/get metadata :table-schem)]
    {:label      table-schem
     :insertText table-schem
     :detail     (str "Schema in " table-cat " catalog")
     :kind       5}))


(defmethod suggestion-data-from-metadata "table" [metadata]
  (let [table-type (js-interop/get metadata :table-type)
        table-schem (js-interop/get metadata :table-schem)
        table-name (js-interop/get metadata :table-name)
        table? (= "TABLE" table-type)]
    {:label      table-name
     :insertText table-name
     :detail     (str (if table? "Table" "View") " in " table-schem " schema")
     :kind       (if table? 15 21)}))


(defmethod suggestion-data-from-metadata "column" [metadata]
  (let [column-name (js-interop/get metadata :column-name)
        type-name (js-interop/get metadata :type-name)]
    {:label      column-name
     :insertText column-name
     :detail     type-name
     :kind       16}))


(defmethod suggestion-data-from-metadata "keyword" [metadata]
  (let [value (js-interop/get metadata :value)]
    {:label      value
     :insertText value
     :detail     "Keyword"
     :kind       17}))


(defmethod suggestion-data-from-metadata "function" [metadata]
  (let [value (js-interop/get metadata :value)]
    {:label           value
     :insertText      (str value "(${1})")
     :detail          "Function"
     :insertTextRules 4
     :kind            1}))


(defmethod suggestion-data-from-metadata "operator" [metadata]
  (let [value (js-interop/get metadata :value)]
    {:label      value
     :insertText value
     :detail     "Operator"
     :kind       11}))


(defmethod suggestion-data-from-metadata :default [metadata]
  {:label      "Unknown"
   :insertText ""
   :detail     (str (js->clj metadata))
   :kind       18})


(def suggestion-data-from-metadata-memo
  (memoize suggestion-data-from-metadata))


(defn generate-suggestions [completions]
  (->> completions
       (map suggestion-data-from-metadata-memo)
       distinct
       (map clj->js)))


(defn find-nested-matches [completions search-pattern]
  (filter (fn [completion]
            (let [id (js-interop/get completion :id)]
              (some? (re-matches search-pattern id))))
          completions))


(defn find-plain-matches [completions search-pattern]
  (filter (fn [completion]
            (let [table-name  (js-interop/get completion :table-name "")
                  table-schem (js-interop/get completion :table-schem "")
                  table-cat   (js-interop/get completion :table-cat "")
                  column-name (js-interop/get completion :column-name "")
                  value       (js-interop/get completion :value "")]
              (or
               (re-matches search-pattern table-cat)
               (re-matches search-pattern table-name)
               (re-matches search-pattern table-schem)
               (re-matches search-pattern column-name)
               (re-matches search-pattern value))))
          completions))


(defn match-completions [type data]
  ;; Even just fetching the precomputed autocomplete blanks takes a while :(
  ;; About 100ms to retrieve a vector containing 110k vectors
  (js-interop/call db :getItem "db-completions"
                   (fn [error completions]
                     (let [candidate (js-interop/get data :candidate)]
                       (when-not (clojure.string/blank? candidate)
                         (let [search-term    (clojure.string/replace candidate #"\." "---")
                               nested?        (some? (re-find #"\." candidate))
                               search-pattern (re-pattern
                                               (if nested?
                                                 (str "(?i)" ".*" search-term ".*")
                                                 (str "(?i)^" search-term ".*")))
                               results        (if nested?
                                                (find-nested-matches completions search-pattern)
                                                (find-plain-matches  completions search-pattern))
                               suggestions (generate-suggestions results)
                               suggestions' (clj->js suggestions)]

                           (js/postMessage #js {:type type :data suggestions'})))))))



;; ----------------------------------------------------------------------------
;;
;; BOOTSTRAP
;;
;; Sets up event type handlers
;;
;; ----------------------------------------------------------------------------


(defn init []
  (js/self.addEventListener "message"
    (fn [^js e]
      (let [payload (.. e -data)
            type (js-interop/get payload :type)
            data (js-interop/get payload :data)]

        (case type
          "generate-tree-view"   (generate-tree-view type)
          "generate-completions" (generate-completions type)
          "match-completions"    (match-completions type data)
         (js/console.warn "Unsupported metadata webworker message type:" type))))))
